import { useCallback, useEffect, useMemo, useState } from "react";
import {
  LIST_PAGE_SIZE_DEFAULT,
  LIST_PAGE_SIZE_OPTIONS,
  sliceItemsForPage,
} from "../utils/listPagination";

/**
 * Client-side list pagination: page size, page index, sliced items, and bar props.
 *
 * @param {unknown[]} items Full filtered/sorted list to paginate.
 * @param {unknown[]} [resetTriggers] When any value changes (by `Object.is`), page resets to 1.
 *   Pass primitives inline, e.g. `[searchTerm, sortConfig.key]`.
 * @param {object} [options]
 * @param {number} [options.initialPageSize]
 * @param {number[]} [options.pageSizeOptions]
 */
export function useListPagination(items, resetTriggers = [], options = {}) {
  const {
    initialPageSize = LIST_PAGE_SIZE_DEFAULT,
    pageSizeOptions = LIST_PAGE_SIZE_OPTIONS,
  } = options;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const totalCount = safeItems.length;

  const sliceResult = useMemo(
    () => sliceItemsForPage(safeItems, page, pageSize),
    [safeItems, page, pageSize]
  );

  const safePage = sliceResult.safePage;
  const pageItems = sliceResult.items;

  useEffect(() => {
    setPage(1);
    // resetTriggers is a caller-provided dependency list (intentional non-literal array).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, resetTriggers);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const setPageSizeAndReset = useCallback((nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  }, []);

  const paginationBarProps = useMemo(
    () => ({
      totalCount,
      page: safePage,
      pageSize,
      pageSizeOptions,
      onPageChange: setPage,
      onPageSizeChange: setPageSizeAndReset,
    }),
    [totalCount, safePage, pageSize, pageSizeOptions, setPageSizeAndReset]
  );

  /** Header checkbox props for "select all on current page" (pass useEntityListChrome `handleSelectAll`). */
  function getSelectAllCheckboxProps(onSelectAll, selectedIds) {
    return {
      checked: pageItems.length > 0 && pageItems.every((obj) => selectedIds.has(obj._id)),
      onChange: (e) => onSelectAll(e, () => pageItems),
      disabled: pageItems.length === 0,
    };
  }

  return {
    pageItems,
    totalCount,
    page: safePage,
    pageSize,
    setPage,
    setPageSize: setPageSizeAndReset,
    paginationBarProps,
    getSelectAllCheckboxProps,
  };
}
